import React, { Component } from "react";
import { DatePicker, Popover } from "antd";
import moment from "moment";

/**
 * VaccineOptional
 *
 * Reusable ANC vaccination block rendered as an "Optional" table with the full
 * column set: Vaccine, Vaccine Name, Given Outside?, Vaccination Site, Given On,
 * Batch, Expiry Date, Remarks. Vaccination entry is optional.
 *
 * Inputs are fully controlled by the parent's `vaccineData` state (NOT the antd
 * Form store), so values (e.g. Given On) survive reloads and no antd Form validation
 * messages fire. Validation is handled by the parent via a notification.
 *
 * A row that is already saved in the shared vaccination record (id > 0) is locked.
 *
 * Props:
 * - vaccineData (array)      : items { name, item, vac_site, vaccine_date, given_outside, batch, expiry_date, remark, id }
 * - vaccineItemList (array)  : vaccine-name master list [{ id, name }]
 * - vaccinationSites (array) : site options [{ id, label }]
 * - allowEdit (bool)
 * - onVaccineDate(date, dateString, index, field)
 * - onGivenOutside(e, index)
 * - onFieldChange(e, index, field)   : text inputs (batch, remark)
 * - onSelectChange(value, index, field) : selects (item, vac_site)
 * - onExpiryDate(date, dateString, index)
 * - disabledPastDate(current)
 */
class VaccineOptional extends Component {
  render() {
    const {
      vaccineData = [],
      vaccineItemList = [],
      vaccinationSites = [],
      allowEdit,
      onVaccineDate,
      onGivenOutside,
      onFieldChange,
      onSelectChange,
      onExpiryDate,
      onAddDose,
      disabledPastDate,
      patientDob,
    } = this.props;

    if (!vaccineData || vaccineData.length === 0) {
      return null;
    }

    // ANC vaccines are for the mother and are NOT age-scheduled like paediatric doses,
    // so there is no Due Now / Missed status here (that logic is paediatric/age-based).
    // The Given On cell is therefore left un-tinted, matching a plain row.
    return (
      <>
        <div className='row mt-4'>
          <div className='col-12'>
            <span className='title'>Immunoprophylaxis</span>
          </div>
        </div>
        <div className='row mt-3'>
          <div className='col-12'>
            <div className='table-responsive'>
              <table className='table table-border'>
                <thead>
                  <tr>
                    <th style={{ width: '14%', textAlign: 'center' }}>VACCINE</th>
                    <th style={{ width: '16%', textAlign: 'center' }}>VACCINE NAME</th>
                    <th style={{ width: '9%', textAlign: 'center' }}>GIVEN OUTSIDE?</th>
                    <th style={{ width: '16%', textAlign: 'center' }}>VACCINATION SITE</th>
                    <th style={{ width: '13%', textAlign: 'center' }}>GIVEN ON</th>
                    <th style={{ width: '11%', textAlign: 'center' }}>BATCH</th>
                    <th style={{ width: '10%', textAlign: 'center' }}>EXPIRY DATE</th>
                    <th style={{ width: '9%', textAlign: 'center' }}>REMARKS</th>
                    <th style={{ width: '4%', textAlign: 'center' }}>INFO</th>
                    <th style={{ width: '5%', textAlign: 'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {vaccineData.map((data, key) => {
                    // A row already saved (id > 0) is locked, same as the Vaccination Record page.
                    const rowDisabled = !allowEdit || (data.id != null && data.id !== 0);
                    return (
                      <tr key={key}>
                        <td>{data.name}</td>
                        <td>
                          {/* Native select with table-form-control, same as the Vaccination Record page. */}
                          <select
                            className="table-form-control"
                            disabled={rowDisabled}
                            value={(data?.item !== undefined && data?.item !== null && data?.item !== '') ? String(data.item) : ''}
                            onChange={(e) => onSelectChange(e.target.value, key, 'item')}
                          >
                            <option value="">Select</option>
                            {(data?.vaccine_list && data.vaccine_list.length > 0)
                              ? data.vaccine_list.map((item, i) => (
                                  <option key={i} value={String(item.id)}>{item.name}</option>
                                ))
                              : (vaccineItemList && vaccineItemList.length > 0
                                  ? vaccineItemList.map((item, i) => (
                                      <option key={i} value={String(item.id)}>{item.name}</option>
                                    ))
                                  : null)}
                          </select>
                        </td>
                        <td className='text-center'>
                          <div className="custom-control custom-checkbox custom-control-inline">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id={'anc_vacc_outside_' + key}
                              checked={data?.given_outside == 1}
                              disabled={rowDisabled}
                              onChange={(e) => onGivenOutside(e, key)}
                            />
                            <label className="custom-control-label" htmlFor={'anc_vacc_outside_' + key}></label>
                          </div>
                        </td>
                        <td>
                          <select
                            className="table-form-control"
                            disabled={rowDisabled}
                            value={(data?.vac_site !== undefined && data?.vac_site !== null && data?.vac_site !== '') ? String(data.vac_site) : ''}
                            onChange={(e) => onSelectChange(e.target.value, key, 'vac_site')}
                          >
                            <option value="">Select</option>
                            {vaccinationSites && vaccinationSites.length > 0
                              ? vaccinationSites.map((site, i) => (
                                  <option key={i} value={String(site.id)}>{site.label}</option>
                                ))
                              : null}
                          </select>
                        </td>
                        <td>
                          <DatePicker
                            format="DD MMM, YYYY"
                            disabled={rowDisabled}
                            disabledDate={disabledPastDate}
                            value={data?.vaccine_date ? moment(data.vaccine_date) : null}
                            onChange={(date, dateString) => onVaccineDate(date, dateString, key, 'vaccine_date')}
                            className='mb-0'
                            style={{ width: '100%', height: '30px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-form-control"
                            placeholder="Type Batch"
                            maxLength="20"
                            disabled={rowDisabled}
                            value={data?.batch || ''}
                            onChange={(e) => onFieldChange(e, key, 'batch')}
                          />
                        </td>
                        <td className="datepicker">
                          <DatePicker
                            picker="month"
                            format="MMM, YYYY"
                            disabled={rowDisabled}
                            value={data?.expiry_date ? moment(data.expiry_date) : null}
                            onChange={(date, dateString) => onExpiryDate(date, dateString, key)}
                            style={{ width: '100%', height: '30px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-form-control remark"
                            placeholder="Type Remark"
                            disabled={rowDisabled}
                            value={data?.remark || ''}
                            onChange={(e) => onFieldChange(e, key, 'remark')}
                          />
                        </td>
                        <td className='text-center'>
                          {/* INFO: who recorded this dose + when (only for saved rows). */}
                          {
                            (data.id != null && data.id !== 0)
                            ?
                              <Popover
                                title={data.staff == null ? "Recorded By" : "Administered By"}
                                placement="topRight"
                                trigger="hover"
                                content={
                                  <div>
                                    <p>{(data.staff == null) ? 'Patient' : (data.staff?.role_id && data.staff?.role_id == 3) ? 'Dr' : 'Nr'} {data.staff ? data.staff.first_name : ''} {data.staff ? data.staff.last_name : ''}{data.givenon ? ', ' + moment(data.givenon).format('D MMM YYYY') : ''}</p>
                                    {data.created_at ? <p><b>Recorded On :</b> {moment(data.created_at).format('D MMM YYYY')}</p> : null}
                                  </div>
                                }
                              >
                                <span className="icon_info" style={{ cursor: 'pointer' }}></span>
                              </Popover>
                            :
                            '-'
                          }
                        </td>
                        <td className='text-center'>
                          {/* ACTION "+": add another editable dose row for the same vaccine. */}
                          {
                            (allowEdit && data.id != null && data.id !== 0)
                            ?
                              <a href="javascript:void(0)" onClick={() => onAddDose && onAddDose(key)}>
                                <span className="icon-plus"></span>
                              </a>
                            :
                            '-'
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default VaccineOptional;
